/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
} from "react-virtualized";
import {
  getReportDataV2,
  getReportPdf,
  refreshReport,
  saveReport,
} from "../../../services/IReportService";
import {
  RESET_REPORT_DATA,
  SET_REPORT_DATA,
  UPDATE_REPORT_DATA,
  REORDER_REPORT_OBSERVATION_PHOTOS,
} from "../../../store/actions";
import Spinner from "../../components/Spinner";
import {
  deepCopyObject,
  formatReportData,
  getAddress,
  getErrorMsg,
  getReportDataForApi,
  getReportKeys,
  isEmptyObject,
  setHeader,
  showToast,
} from "../../shared/helpers";
import { regexes } from "../../shared/regexes";
import AddPageButton from "./Components/AddPageButton";
import DocumentNavigator from "./Components/DocumentNavigator";
import EditTextModal from "./Components/EditTextModal";
import Background from "./Components/ReportPages/Background";
import ExtraPage from "./Components/ReportPages/ExtraPage";
import Intro from "./Components/ReportPages/Intro";
import ReportTerms from "./Components/ReportPages/ReportTerms";
import ObservationComments from "./Components/ReportPages/ObservationsPages/ObservationComments";
import ObservationPhotos from "./Components/ReportPages/ObservationsPages/ObservationPhotos";
import ObservationTests from "./Components/ReportPages/ObservationsPages/ObservationTests";
import Protocol from "./Components/ReportPages/Protocol";
import ReportInspection from "./Components/ReportPages/ReportInspection";
import SignatureModal from "./Components/SignatureModal";
import TemplatePage from "./Components/ReportPages/TemplatePage";
import TOC from "./Components/ReportPages/TOC";
import { HIDE_MODAL_CONFIRM } from "../../../store/actions";
import "./style.css";

const ConfirmationModal = React.lazy(
  () => import("../Settings/Users/ConfirmationModal")
);

const reportPages = {
  cover_page: Intro,
  toc: TOC,
  background: Background,
  inspection: ReportInspection,
  observations: "observations",
  specific_decontamination: (props) => (
    <TemplatePage templateKey={"specific_decontamination"} {...props} />
  ),
  generel_decontamination: (props) => (
    <TemplatePage templateKey={"generel_decontamination"} {...props} />
  ),
  sampling: (props) => <TemplatePage templateKey={"sampling"} {...props} />,
  laboratory: (props) => <TemplatePage templateKey={"laboratory"} {...props} />,
  interpreting: (props) => (
    <TemplatePage templateKey={"interpreting"} {...props} />
  ),
  health_effects: (props) => (
    <TemplatePage templateKey={"health_effects"} {...props} />
  ),
  reference: (props) => <TemplatePage templateKey={"reference"} {...props} />,
  limitation: (props) => <TemplatePage templateKey={"limitation"} {...props} />,
};

const fetchDataAndUpdate = async (dispatch, insp_id, section, controller) => {
  try {
    const inspWorkflow = await getReportDataV2(
      insp_id,
      section,
      controller.signal
    );

    if (controller.signal.aborted) {
      throw new Error("Request aborted");
    }

    return { [section]: inspWorkflow.data[section] };
  } catch (error) {
    if (error.name === "AbortError") {
      // Request was cancelled, return empty object
      return { [section]: {} };
    } else {
      // Log error for debugging but don't break the UI
      console.error(`Error fetching ${section}:`, error);
      return { [section]: {} };
    }
  }
};
export default function InvestigationReport() {
  const [scrollToRow, setScrollToRow] = useState(0);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { insp_id } = useParams();
  const editModal = useSelector((state) => state.editModal);
  const observations = useSelector((state) => state.report?.observations?.data);
  const report = useSelector((state) => state.report);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [isFetchingComplete, setIsFetchingComplete] = useState(true);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [signatureName, setSignatureName] = useState("");
  const [signatureIdx, setSignatureIdx] = useState(null);
  const [signatureDate, setSignatureDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [signatureTime, setSignatureTime] = useState(dayjs().toISOString());

  const [pages, setPages] = useState([]);
  const list = useRef(null);

  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 800,
    })
  );
  const setReportData = (res) => {
    const content = res?.data?.cover_page?.report?.terms?.content;
    if (content) {
      res.data.cover_page.report.terms.content = content.replace(
        regexes.styleAttribute,
        ""
      );
    }

    formatReportData(res.data);
    setHeader(dispatch, {
      title:
        "Inspection Report - " +
          getAddress(res?.data?.cover_page?.property?.address) ?? "",
    });
    dispatch({ type: SET_REPORT_DATA, payload: res.data });
  };

  const updateInspectionSignature = (updates) => {
    if (isEmptyObject(report)) return;
    const nextReport = deepCopyObject(report);
    nextReport.inspection = nextReport.inspection || {};
    nextReport.inspection.signature = {
      ...(nextReport.inspection.signature || {}),
      ...updates,
    };
    dispatch({ type: UPDATE_REPORT_DATA, payload: nextReport });
  };

  const controllers = useRef([]);

  useEffect(() => {
    let abort = false;
    const fetchData = async () => {
      setIsLoading(true);
      setIsFetchingComplete(false);

      const sections = [
        "cover_page",
        "toc",
        "background",
        "inspection",
        "observations",
        "specific_decontamination",
        "generel_decontamination",
        "sampling",
        "laboratory",
        "interpreting",
        "health_effects",
        "reference",
        "limitation",
        "extra_pages",
      ];

      let updated_report = {};
      try {
        for (const section of sections) {
          const controller = new AbortController();
          controllers.current.push(controller);

          if (abort) {
            break;
          }

          const result = await fetchDataAndUpdate(
            dispatch,
            insp_id,
            section,
            controller
          );

          updated_report = {
            ...updated_report,
            ...result,
          };

          if (section === "cover_page") {
            const content = updated_report?.cover_page?.report?.terms?.content;
            if (content) {
              updated_report.cover_page.report.terms.content = content.replace(
                regexes.styleAttribute,
                ""
              );
            }
            setHeader(dispatch, {
              title:
                "Inspection Report - " +
                (getAddress(updated_report?.cover_page?.property?.address) ??
                  ""),
            });
          }
          if (section === "toc") {
            updated_report = deepCopyObject(updated_report);

            formatReportData(updated_report);
            dispatch({
              type: UPDATE_REPORT_DATA,
              payload: updated_report,
            });
          }
          if (section === "observations") {
            setIsLoading(false);
            updated_report = deepCopyObject(updated_report);
            formatReportData(updated_report);
            dispatch({
              type: UPDATE_REPORT_DATA,
              payload: updated_report,
            });
          } else {
            dispatch({
              type: UPDATE_REPORT_DATA,
              payload: result,
            });
          }
        }
        setIsLoading(false);
        setIsFetchingComplete(true);
      } catch (error) {
        // Handle errors as needed
        console.error("Error fetching data:", error);
        setIsLoading(false);
        setIsFetchingComplete(true);
      }
    };

    fetchData();

    return () => {
      abort = true;
      controllers.current.forEach((controller) => {
        controller.abort();
      });
      controllers.current = [];
      dispatch({
        type: RESET_REPORT_DATA,
        payload: {},
      });
    };
  }, []);

  useEffect(() => {
    if (!isEmptyObject(report)) {
      const signature = report?.inspection?.signature || {};
      if (signature?.image && signature.image !== signatureDataUrl) {
        setSignatureDataUrl(signature.image);
      }
      if (signature?.name && signature.name !== signatureName) {
        setSignatureName(signature.name);
      }
      if (signature?.index !== undefined && signature.index !== signatureIdx) {
        setSignatureIdx(signature.index);
      }
      if (signature?.date && signature.date !== signatureDate) {
        setSignatureDate(signature.date);
      }
      if (signature?.time && signature.time !== signatureTime) {
        setSignatureTime(signature.time);
      }
      const tempPages = [];
      const reportKeys = getReportKeys(report);
      reportKeys?.forEach((key) => {
        if (key === "cover_page") {
          tempPages.push(reportPages[key]);
          tempPages.push((props) => <ReportTerms {...props} />);
        } else if (key === "observations") {
          observations?.forEach((o, i) => {
            o?.observations?.forEach((obs, j) => {
              if (obs?.comments?.length > 0) {
                tempPages.push((props) => (
                  <ObservationComments
                    insp_id={insp_id}
                    sethandleSaveState={sethandleSaveState}
                    index={j}
                    roomIndex={i}
                    comments={obs.comments}
                    {...props}
                  />
                ));
              }
              if (obs?.images?.length > 0) {
                tempPages.push((props) => (
                  <ObservationPhotos
                    index={j}
                    roomIndex={i}
                    photos={obs?.images}
                    title={obs?.location ?? ""}
                    onPhotosReorder={onPhotosReorder}
                    {...props}
                  />
                ));
              }
              if (obs?.tests?.length > 0) {
                tempPages.push((props) => (
                  <ObservationTests
                    index={j}
                    roomIndex={i}
                    tests={obs?.tests}
                    {...props}
                  />
                ));
              }
            });

            tempPages.push((props) => (
              <Protocol roomIndex={i} protocols={o.protocol_text} {...props} />
            ));
          });
        } else if (key === "inspection") {
          tempPages.push((props) => (
            <ReportInspection
              {...props}
              signatureDataUrl={signatureDataUrl}
              signatureDate={signatureDate}
              signatureTime={signatureTime}
              onSignatureDateChange={(value) => {
                setSignatureDate(value);
                updateInspectionSignature({ date: value });
              }}
              onSignatureTimeChange={(value) => {
                setSignatureTime(value);
                updateInspectionSignature({ time: value });
              }}
              onOpenSignatureModal={() => setSignatureModalOpen(true)}
            />
          ));
        } else {
          tempPages.push(reportPages[key]);
        }
      });
      if (Array.isArray(report?.extra_pages)) {
        report.extra_pages.forEach((extra, i) => {
          tempPages.push((props) => (
            <ExtraPage {...props} data={extra} index={i} />
          ));
        });
      }
      tempPages.push((props) => <AddPageButton {...props} />);
      setPages(tempPages);
    }
  }, [
    report,
    signatureDataUrl,
    signatureDate,
    signatureTime,
    signatureName,
    signatureIdx,
  ]);

  useEffect(() => {
    list?.current?.scrollToRow(scrollToRow);
    const id = setTimeout(() => {
      list?.current?.scrollToRow(scrollToRow);
    }, 250);

    return () => clearTimeout(id);
  }, [scrollToRow]);

  const handlePublish = () => {
    setIsPdfLoading(true);
    getReportPdf(getReportDataForApi(report), insp_id)
      .then(() => {
        showToast(
          "Inspection Report will be available in Documents listing shortly.",
          true
        );
      })
      .catch((err) => {
        console.log(err);
        showToast("Error while generating pdf");
      })
      .finally(() => setIsPdfLoading(false));
  };

  const onPhotosReorder = (reorderedPhotos, roomIndex, observationIndex) => {
    dispatch({
      type: REORDER_REPORT_OBSERVATION_PHOTOS,
      payload: {
        photos: reorderedPhotos,
        roomIndex,
        observationIndex,
      },
    });
  };

  const [handleSaveState, sethandleSaveState] = useState(false);
  useEffect(() => {
    if (handleSaveState) {
      handleSave();
      sethandleSaveState(false);
    }
  }, [report]);

  const handleSave = () => {
    setIsPdfLoading(true);
    debugger;
    saveReport(insp_id, { data: JSON.stringify(report) })
      .then(() => {
        showToast("Report saved successfully", true);
      })
      .catch((err) => {
        console.log(err);
        showToast(getErrorMsg(err) || "Something went wrong");
      })
      .finally(() => setIsPdfLoading(false));
  };

  const handleRefresh = (setDisableBtns) => {
    setDisableBtns(true);
    setIsPdfLoading(true);
    refreshReport(insp_id)
      .then((res) => {
        showToast("Report refreshed successfully", true);
        setReportData(res);
        setShowRefreshModal(false);
      })
      .catch((err) => {
        console.log(err);
        showToast(getErrorMsg(err) || "Something went wrong");
      })
      .finally(() => {
        setIsPdfLoading(false);
        setDisableBtns(false);
      });
  };

  return (
    <div id="report" className="d-flex flex-row">
      <Spinner reactIf={isLoading || !isFetchingComplete} />
      {!isLoading &&
      isFetchingComplete &&
      !isEmptyObject(report) &&
      pages.length > 0 ? (
        <>
          <div className="ls py-2 pe-0">
            <DocumentNavigator
              list={list}
              scrollToRow={scrollToRow}
              setScrollToRow={setScrollToRow}
            />
          </div>
          <div id="reportScroll" className="rs">
            <div className="report-action-container shadow">
              <Button
                className="report-save-btn dis-loading"
                onClick={handleSave}
                disabled={isPdfLoading || !isFetchingComplete}
              >
                Save
              </Button>
              <Button
                className="report-save-btn dis-loading"
                onClick={() => setShowRefreshModal(true)}
                disabled={isPdfLoading}
              >
                Refresh
              </Button>
              {/* <DownloadPDF report={report} /> */}
              <Button
                className="dis-loading"
                onClick={handlePublish}
                disabled={isPdfLoading || !isFetchingComplete}
              >
                Publish
              </Button>
            </div>
            <AutoSizer>
              {({ width, height }) => (
                <List
                  width={width}
                  height={height}
                  rowHeight={cache.current.rowHeight}
                  deferredMeasurementCache={cache.current}
                  rowCount={pages.length}
                  ref={list}
                  scrollToAlignment="start"
                  overscanRowCount={2}
                  rowRenderer={({ key, index, style, parent }) => {
                    const Page = pages[index];
                    if (!Page) return null;
                    return (
                      <CellMeasurer
                        key={key}
                        cache={cache.current}
                        parent={parent}
                        columnIndex={0}
                        rowIndex={index}
                      >
                        {({ measure, registerChild }) => {
                          return (
                            <div ref={registerChild} style={style}>
                              <Page id={`page${index}`} />
                            </div>
                          );
                        }}
                      </CellMeasurer>
                    );
                  }}
                />
              )}
            </AutoSizer>
          </div>
          <EditTextModal
            {...editModal}
            sethandleSaveState={sethandleSaveState}
          />
        </>
      ) : null}
      <Suspense fallback={null}>
        <ConfirmationModal
          show={showRefreshModal}
          onHide={() => setShowRefreshModal(false)}
          msg={
            "Refreshing this document will erase ALL edited information and create a new draft. Are you sure you want to refresh this document?"
          }
          onDelete={handleRefresh}
          yesBtnText="Refresh"
          noBtnText="Cancel"
        />
        <ConfirmationModal
          show={editModal.showConfirm}
          onHide={() =>
            dispatch({
              type: HIDE_MODAL_CONFIRM,
            })
          }
          msg={"Are you sure you want to delete this photo?"}
          onDelete={editModal.onDelete}
          yesBtnText="Delete"
          noBtnText="Cancel"
          hideOnPress
        />
      </Suspense>
      <SignatureModal
        show={signatureModalOpen}
        onHide={() => setSignatureModalOpen(false)}
        onSave={(signature) => {
          setSignatureName(signature.signature || "");
          setSignatureDataUrl(signature.signatureImage || "");
          setSignatureIdx(signature.signatureIdx ?? null);
          updateInspectionSignature({
            name: signature.signature || "",
            image: signature.signatureImage || "",
            index: signature.signatureIdx ?? null,
          });
          setSignatureModalOpen(false);
        }}
        selectedSignatureName={signatureName}
        selectedSignatureImage={signatureDataUrl}
        selectedSignatureIdx={signatureIdx}
      />
    </div>
  );
}
